import { supabase } from '@/lib/supabase';
import type { RestaurantWithProducts } from './restaurant.types';
import { expandSearchTerm } from './search-synonyms';

// Fuzzy search 결과 타입 정의
interface FuzzySearchResult {
  id: string;
  name: string;
  address: string;
  bestmenu: string;
  store_type: string;
  is_open: boolean;
  image_url: string | null;
  rating: number;
  created_at: string;
  similarity_score: number;
}

// Products를 포함한 검색 결과 타입
interface ProductSearchResult {
  id: string;
  name: string;
  address: string;
  bestmenu: string;
  bestmenu1: string | null;
  bestmenu2: string | null;
  bestmenu3: string | null;
  store_type: string;
  is_open: boolean;
  image_url: string | null;
  rating: number;
  created_at: string;
  similarity_score: number;
  matched_product: string | null;
}

export class RestaurantSearchService {

  /**
   * 향상된 검색 기능 - Fuzzy search와 동의어 시스템을 통합
   */
  async searchRestaurants(
    searchTerm: string,
    filters?: {
      storeType?: string;
      isOpen?: boolean;
      hasProducts?: boolean;
      maxDistance?: number;
      userLocation?: { lat: number; lng: number };
    }
  ): Promise<RestaurantWithProducts[]> {
    try {
      // 검색어가 비어있으면 전체 목록 반환
      if (!searchTerm.trim()) {
        return this.getAllRestaurants(filters);
      }

      // 1. 동의어 확장
      const expandedTerms = expandSearchTerm(searchTerm);
      console.log('Expanded search terms:', expandedTerms);

      // 2. Fuzzy 검색 (pg_trgm 활용 - bestmenu1/2/3 포함)
      const fuzzyResults = await this.performFuzzySearch(searchTerm);
      
      // 3. Products 테이블 검색 (메뉴명 검색)
      const productResults = await this.searchWithProducts(searchTerm);
      
      // 4. 동의어로 추가 검색
      const synonymResults = await this.searchWithSynonyms(expandedTerms, filters);
      
      // 5. 결과 병합 및 중복 제거
      const mergedResults = this.mergeAndRankResults(
        fuzzyResults,
        productResults,
        synonymResults
      );

      // 5. 필터 적용
      let filteredResults = mergedResults;
      
      if (filters?.isOpen !== undefined) {
        filteredResults = filteredResults.filter(r => r.is_open === filters.isOpen);
      }

      if (filters?.storeType) {
        filteredResults = filteredResults.filter(r => r.store_type === filters.storeType);
      }

      // TODO: Add distance filtering if userLocation is provided
      
      return filteredResults;
    } catch (error) {
      console.error('Search restaurants error:', error);
      // Fallback to basic search
      return this.basicSearch(searchTerm, filters);
    }
  }

  /**
   * pg_trgm을 활용한 Fuzzy 검색
   */
  private async performFuzzySearch(searchTerm: string): Promise<RestaurantWithProducts[]> {
    try {
      const { data, error } = await supabase
        .rpc('search_restaurants_fuzzy', {
          search_term: searchTerm,
          similarity_threshold: 0.2 // 20% 이상 유사도
        });

      if (error) {
        console.error('Fuzzy search error:', error);
        return [];
      }

      // RPC 결과를 RestaurantWithProducts 형태로 변환
      const restaurants = await Promise.all(
        (data as FuzzySearchResult[]).map(async (result) => {
          // 각 레스토랑의 전체 정보를 가져옴
          const { data: fullData } = await supabase
            .from('restaurants')
            .select(`
              *,
              business:businesses(id, business_name, business_number, representative_name, is_verified),
              products(
                id,
                name,
                original_price,
                image_url,
                offers:product_offers(
                  id,
                  offer_type,
                  discount_rate,
                  discounted_price,
                  is_active,
                  is_current,
                  valid_from,
                  valid_until
                ),
                stock:product_stock(
                  date,
                  current_stock
                )
              )
            `)
            .eq('id', result.id)
            .single();

          return {
            ...fullData,
            similarity_score: result.similarity_score
          } as RestaurantWithProducts & { similarity_score: number };
        })
      );

      return restaurants.filter(r => r !== null);
    } catch (error) {
      console.error('Fuzzy search error:', error);
      return [];
    }
  }

  /**
   * 동의어를 사용한 검색
   */
  private async searchWithSynonyms(
    terms: string[],
    filters?: any
  ): Promise<RestaurantWithProducts[]> {
    try {
      // 각 동의어에 대한 OR 조건 생성
      const conditions = terms.flatMap(term => [
        `name.ilike.%${term}%`,
        `address.ilike.%${term}%`,
        `bestmenu.ilike.%${term}%`,
        `store_type.ilike.%${term}%`
      ]);

      let query = supabase
        .from('restaurants')
        .select(`
          *,
          business:businesses(id, business_name, business_number, representative_name, is_verified),
          products(
            id,
            name,
            original_price,
            image_url,
            offers:product_offers(
              id,
              offer_type,
              discount_rate,
              discounted_price,
              is_active,
              is_current,
              valid_from,
              valid_until
            ),
            stock:product_stock(
              date,
              current_stock
            )
          )
        `)
        .or(conditions.join(','));

      if (filters?.hasProducts) {
        const today = new Date().toISOString().split('T')[0];
        query = query
          .eq('products.offers.is_active', true)
          .eq('products.stock.date', today)
          .gt('products.stock.current_stock', 0);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Synonym search error:', error);
        return [];
      }

      return (data as unknown as RestaurantWithProducts[]) || [];
    } catch (error) {
      console.error('Synonym search error:', error);
      return [];
    }
  }

  /**
   * Products 테이블에서 메뉴명으로 검색
   */
  private async searchWithProducts(searchTerm: string): Promise<RestaurantWithProducts[]> {
    try {
      const { data, error } = await supabase
        .rpc('search_restaurants_with_products', {
          search_term: searchTerm,
          similarity_threshold: 0.2
        });

      if (error) {
        console.error('Product search error:', error);
        return [];
      }

      // RPC 결과를 RestaurantWithProducts 형태로 변환
      const restaurants = await Promise.all(
        (data as ProductSearchResult[]).map(async (result) => {
          // 각 레스토랑의 전체 정보를 가져옴
          const { data: fullData } = await supabase
            .from('restaurants')
            .select(`
              *,
              business:businesses(id, business_name, business_number, representative_name, is_verified),
              products(
                id,
                name,
                original_price,
                image_url,
                offers:product_offers(
                  id,
                  offer_type,
                  discount_rate,
                  discounted_price,
                  is_active,
                  is_current,
                  valid_from,
                  valid_until
                ),
                stock:product_stock(
                  date,
                  current_stock
                )
              )
            `)
            .eq('id', result.id)
            .single();

          return {
            ...fullData,
            similarity_score: result.similarity_score,
            matched_product: result.matched_product
          } as RestaurantWithProducts & { similarity_score: number; matched_product: string | null };
        })
      );

      return restaurants.filter(r => r !== null);
    } catch (error) {
      console.error('Product search error:', error);
      return [];
    }
  }

  /**
   * 결과 병합 및 랭킹
   */
  private mergeAndRankResults(
    fuzzyResults: any[],
    productResults: any[],
    synonymResults: RestaurantWithProducts[]
  ): RestaurantWithProducts[] {
    const resultMap = new Map<string, any>();
    
    // Fuzzy 결과 (높은 우선순위)
    fuzzyResults.forEach(r => {
      if (r && r.id) {
        resultMap.set(r.id, {
          ...r,
          searchScore: r.similarity_score || 1
        });
      }
    });
    
    // Product 검색 결과 (중간 우선순위)
    productResults.forEach(r => {
      if (r && r.id) {
        const existing = resultMap.get(r.id);
        if (!existing || existing.searchScore < (r.similarity_score || 0.8)) {
          resultMap.set(r.id, {
            ...r,
            searchScore: r.similarity_score || 0.8
          });
        }
      }
    });
    
    // 동의어 결과 (낮은 우선순위)
    synonymResults.forEach(r => {
      if (r && r.id && !resultMap.has(r.id)) {
        resultMap.set(r.id, {
          ...r,
          searchScore: 0.5
        });
      }
    });
    
    // 점수 기준으로 정렬
    return Array.from(resultMap.values())
      .sort((a, b) => {
        // 먼저 검색 점수로 정렬
        if (a.searchScore !== b.searchScore) {
          return b.searchScore - a.searchScore;
        }
        // 그 다음 평점으로 정렬
        return (b.rating || 0) - (a.rating || 0);
      });
  }

  /**
   * 기본 검색 (Fallback)
   */
  private async basicSearch(
    searchTerm: string,
    filters?: any
  ): Promise<RestaurantWithProducts[]> {
    try {
      let query = supabase
        .from('restaurants')
        .select(`
          *,
          business:businesses(id, business_name, business_number, representative_name, is_verified),
          products(
            id,
            name,
            original_price,
            image_url,
            offers:product_offers(
              id,
              offer_type,
              discount_rate,
              discounted_price,
              is_active,
              is_current,
              valid_from,
              valid_until
            ),
            stock:product_stock(
              date,
              current_stock
            )
          )
        `)
        .or(`name.ilike.%${searchTerm}%,address.ilike.%${searchTerm}%,bestmenu.ilike.%${searchTerm}%`);

      if (filters?.storeType) {
        query = query.eq('store_type', filters.storeType);
      }

      if (filters?.isOpen !== undefined) {
        query = query.eq('is_open', filters.isOpen);
      }

      const { data, error } = await query.order('rating', { ascending: false });

      if (error) {
        console.error('Basic search error:', error);
        return [];
      }

      return (data as unknown as RestaurantWithProducts[]) || [];
    } catch (error) {
      console.error('Basic search error:', error);
      return [];
    }
  }

  /**
   * 전체 레스토랑 목록 조회
   */
  private async getAllRestaurants(filters?: any): Promise<RestaurantWithProducts[]> {
    try {
      let query = supabase
        .from('restaurants')
        .select(`
          *,
          business:businesses(id, business_name, business_number, representative_name, is_verified),
          products(
            id,
            name,
            original_price,
            image_url,
            offers:product_offers(
              id,
              offer_type,
              discount_rate,
              discounted_price,
              is_active,
              is_current,
              valid_from,
              valid_until
            ),
            stock:product_stock(
              date,
              current_stock
            )
          )
        `);

      if (filters?.storeType) {
        query = query.eq('store_type', filters.storeType);
      }

      if (filters?.isOpen !== undefined) {
        query = query.eq('is_open', filters.isOpen);
      }

      const { data, error } = await query.order('rating', { ascending: false });

      if (error) {
        console.error('Get all restaurants error:', error);
        return [];
      }

      return (data as unknown as RestaurantWithProducts[]) || [];
    } catch (error) {
      console.error('Get all restaurants error:', error);
      return [];
    }
  }
}

export const restaurantSearchService = new RestaurantSearchService();